import { packageText as pm, usePackageManagementLanguage, packageFailure, packageFailureText, type PackageFailure } from "../../i18n/packageManagementPresentation";
/**
 * PlexCredentialModal
 *
 * Collects plex.tv Email, Password, and optional 2FA code before a Plex update.
 * The backend requires these to authenticate the PlexPass download.
 *
 * Security contract:
 *   - Password and OTP are NEVER logged to the console
 *   - Values are NEVER written to localStorage or sessionStorage
 *   - Credentials are sent once via POST body then discarded
 *   - Closing without submitting creates no job
 */
import { type FormEvent, useCallback, useEffect, useId, useRef, useState } from "react";
import { useDialogFocus } from "../../hooks/useDialogFocus";
import { createPortal } from "react-dom";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { Spinner } from "../ui/Spinner";
import { cn } from "../../utils/cn";
import { PlexCredentialFields } from "./PlexCredentialFields";

// ── Types ─────────────────────────────────────────────────────────────────────

export type PlexCredentials = {
  plexEmail: string;
  plexPassword: string;
  plexOtp: string;
};

type PlexCredentialModalProps = {
  open: boolean;
  /** Called with credentials when the user submits. Callee fires the request. */
  onSubmit: (credentials: PlexCredentials) => Promise<void>;
  /** Called when the user cancels or dismisses without submitting. */
  onClose: () => void;
  /**
   * The action the credentials authorize — drives the user-facing copy.
   * "update" (default) for the update flow; "install" / "reinstall" for the
   * install dialog so the button + privacy notice read correctly.
   */
  actionVerb?: "update" | "install" | "reinstall";
};

// ── Component ─────────────────────────────────────────────────────────────────

export const PlexCredentialModal = ({ open, onSubmit, onClose, actionVerb = "update" }: PlexCredentialModalProps) => {
  usePackageManagementLanguage();
  const titleId = useId();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<PackageFailure | null>(null);

  const emailRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Reset state when modal opens so re-triggers start fresh
  useEffect(() => {
    if (open) {
      setEmail("");
      setPassword("");
      setOtp("");
      setShowPassword(false);
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  useDialogFocus(dialogRef, { onClose, initialFocusRef: emailRef, canClose: !submitting, enabled: open });

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!email.trim() || !password) return;

      setSubmitting(true);
      setError(null);

      try {
        await onSubmit({ plexEmail: email.trim(), plexPassword: password, plexOtp: otp.trim() });
        // Modal is closed by parent after a successful job creation
      } catch (err) {
        const msg = packageFailure(err, actionVerb === "update" ? "updateStartFailed" : actionVerb === "reinstall" ? "reinstallStartFailed" : "installStartFailed");
        setError(msg);
        setSubmitting(false);
      }
    },
    [email, password, otp, onSubmit, actionVerb],
  );

  const handleClose = useCallback(() => {
    if (!submitting) onClose();
  }, [submitting, onClose]);

  if (!open) return null;

  const canSubmit = email.trim().length > 0 && password.length > 0 && !submitting;

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          "w-full max-w-md rounded-xl border border-border/50 bg-card shadow-xl",
          "animate-in fade-in-0 zoom-in-95 duration-200",
        )}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-border/40 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-500/10">
              <Icon icon="lucide:lock" className="h-4 w-4 text-amber-500" aria-hidden="true" />
            </div>
            <div>
              <h2 id={titleId} className="text-base font-semibold text-foreground">
                {pm("plexSignInRequired")}</h2>
              <p className="text-xs text-muted-foreground">
                {pm("plexAuthentication")}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            aria-label={pm("closeDialog")}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md hover:bg-white/[0.06] transition-colors disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
          >
            <Icon icon="lucide:x" className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={(e) => { void handleSubmit(e); }} className="px-5 py-4 space-y-4">
          {/* Privacy notice */}
          <div className="flex items-start gap-2 rounded-lg bg-muted/30 border border-border/30 px-3 py-2.5">
            <Icon icon="lucide:shield-check" className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              {pm("plexCredentialsUse")}
            </p>
          </div>

          <PlexCredentialFields
            email={email}
            password={password}
            otp={otp}
            showPassword={showPassword}
            disabled={submitting}
            emailRef={emailRef}
            idScope="modal"
            onEmailChange={setEmail}
            onPasswordChange={setPassword}
            onOtpChange={setOtp}
            onToggleShowPassword={() => setShowPassword((prev) => !prev)}
          />

          {/* Error */}
          {error && (
            <div role="alert" className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5">
              <Icon icon="lucide:alert-circle" className="h-4 w-4 text-destructive shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-xs text-destructive-text">{packageFailureText(error)}</p>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleClose}
              disabled={submitting}
            >
              {pm("cancel")}</Button>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={!canSubmit}
              className="gap-1.5"
            >
              {submitting && <Spinner size="4" />}
              {submitting ? pm(actionVerb === "update" ? "startingUpdate" : actionVerb === "reinstall" ? "startingReinstall" : "startingInstall") : pm(actionVerb === "update" ? "updateApp" : actionVerb === "reinstall" ? "reinstallApp" : "installApp", { app: "Plex" })}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};
